import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Edit, Trash2, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import SignatureImage from '../shared/SignatureImage';

const Orders = ({ user }) => {
  // Format date without timezone conversion (for date-only fields)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day).toLocaleDateString();
  };

  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return tab || 'all';
  });
  const [products, setProducts] = useState([]);

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    installationDate: '',
    storeInvoiceNumber: '',
    items: [],
    notes: ''
  });

  useEffect(() => {
    fetchOrders();
    fetchProducts();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, activeTab]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const filterOrders = () => {
    if (activeTab === 'all') {
      setFilteredOrders(orders);
    } else {
      setFilteredOrders(orders.filter(o => o.status === activeTab));
    }
  };

  const handleEditOrder = (order) => {
    setEditFormData({
      customerName: order.customer.name,
      customerEmail: order.customer.email,
      customerPhone: order.customer.phone,
      customerAddress: order.customer.address,
      installationDate: order.installationDate,
      storeInvoiceNumber: order.storeInvoiceNumber || '',
      items: order.items.map(item => ({ ...item })),
      notes: order.notes || ''
    });
    setSelectedOrder(order);
    setEditMode(true);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/orders/${selectedOrder.id}`, editFormData, { withCredentials: true });
      toast.success('Order updated and resubmitted successfully');
      setEditMode(false);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const addItem = () => {
    setEditFormData({
      ...editFormData,
      items: [...editFormData.items, { productId: '', serviceId: '', name: '', price: 0, spiff: 0, qty: 1 }]
    });
  };

  const removeItem = (index) => {
    setEditFormData({
      ...editFormData,
      items: editFormData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...editFormData.items];
    newItems[index][field] = value;

    if (field === 'serviceId' && value) {
      const product = products.find(p => p.id === newItems[index].productId);
      if (product) {
        const service = product.services.find(s => s.id === value);
        if (service) {
          newItems[index].name = service.name;
          newItems[index].price = service.price;
          newItems[index].spiff = service.spiff;
          if (typeof service.notes === 'string' && service.notes.length > 0) {
            newItems[index].notes = service.notes;
          }
        }
      }
    }

    setEditFormData({ ...editFormData, items: newItems });
  };

  const getBadgeClass = (status) => {
    const classes = {
      pending: 'badge-pending',
      approved: 'badge-approved',
      rejected: 'badge-rejected',
      in_process: 'badge-in-process'
    };
    return classes[status] || 'badge-pending';
  };

  const canEdit = (order) => {
    return order.status === 'pending' || order.status === 'rejected';
  };

  const total = editFormData.items.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const totalSpiff = editFormData.items.reduce((sum, item) => sum + (item.spiff * item.qty), 0);

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '32px' }}>Orders</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: '8px' }}>
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all">All Orders</TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="in_process" data-testid="tab-in-process">In Process</TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">Completed ({orders.filter(o => o.status === 'completed').length})</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={activeTab}>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Installation Date</th>
                  <th>Total</th>
                  <th>Spiff</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map(order => (
                  <tr key={order.id} data-testid={`order-row-${order.id}`}>
                    <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                      {order.id.substring(0, 8)}
                      {order.orderType === 'custom' && (
                        <span style={{ marginLeft: '8px', fontSize: '11px', padding: '2px 6px', background: '#8B5CF6', color: 'white', borderRadius: '4px' }}>CUSTOM</span>
                      )}
                    </td>
                    <td>{order.customer.name}</td>
                    <td>{formatDate(order.installationDate)}</td>
                    <td style={{ fontWeight: '600' }}>{order.totals.total !== null ? `$${order.totals.total}` : 'Pending'}</td>
                    <td style={{ fontWeight: '600', color: '#4CAF50' }}>{order.totals.spiffTotal !== null ? `$${order.totals.spiffTotal}` : 'Pending'}</td>
                    <td>
                      <span className={`badge ${getBadgeClass(order.status)}`}>
                        {order.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '8px 16px', fontSize: '13px' }}
                          onClick={() => {
                            setSelectedOrder(order);
                            setEditMode(false);
                          }}
                          data-testid={`view-order-${order.id}`}
                        >
                          View
                        </button>
                        {canEdit(order) && (
                          <button
                            className="btn btn-primary"
                            style={{ padding: '8px 16px', fontSize: '13px' }}
                            onClick={() => handleEditOrder(order)}
                            data-testid={`edit-order-${order.id}`}
                          >
                            <Edit size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredOrders.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
                No orders found.
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedOrder} onOpenChange={() => { setSelectedOrder(null); setEditMode(false); }}>
        <DialogContent style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
          <DialogHeader>
            <DialogTitle>
              {editMode ? 'Edit Order' : 'Order Details'} - {selectedOrder?.id.substring(0, 8)}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div>
              {!editMode ? (
                // View Mode
                <div>
                  <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span className={`badge ${getBadgeClass(selectedOrder.status)}`}>
                        {selectedOrder.status.replace('_', ' ')}
                      </span>
                      {selectedOrder.orderType === 'custom' && (
                        <span style={{ padding: '6px 12px', background: '#8B5CF6', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '600' }}>
                          CUSTOM ORDER
                        </span>
                      )}
                      {selectedOrder.orderType === 'custom' && !selectedOrder.pricingApproved && (
                        <span style={{ padding: '6px 12px', background: '#F59E0B', color: 'white', borderRadius: '6px', fontSize: '13px', fontWeight: '600' }}>
                          AWAITING PRICING
                        </span>
                      )}
                    </div>
                    {canEdit(selectedOrder) && (
                      <button
                        className="btn btn-primary"
                        onClick={() => handleEditOrder(selectedOrder)}
                        data-testid="edit-order-button"
                      >
                        <Edit size={16} />
                        Edit Order
                      </button>
                    )}
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Customer Information</h3>
                    <div style={{ background: '#FAFAFA', padding: '16px', borderRadius: '12px' }}>
                      <p><strong>Name:</strong> {selectedOrder.customer.name}</p>
                      <p><strong>Email:</strong> {selectedOrder.customer.email}</p>
                      <p><strong>Phone:</strong> {selectedOrder.customer.phone}</p>
                      <p><strong>Address:</strong> {selectedOrder.customer.address}</p>
                      <p><strong>Installation Date:</strong> {formatDate(selectedOrder.installationDate)}</p>
                      {selectedOrder.storeInvoiceNumber && (
                        <p><strong>Store Invoice #:</strong> {selectedOrder.storeInvoiceNumber}</p>
                      )}
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Items</h3>
                    {selectedOrder.items.map((item, idx) => (
                      <div key={idx} style={{ background: '#FAFAFA', padding: '16px', borderRadius: '12px', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontWeight: '600' }}>{item.name}</span>
                          <span style={{ fontWeight: '700' }}>
                            {item.price !== null ? `$${item.price} x ${item.qty}` : 'Price pending'}
                          </span>
                        </div>
                        {item.notes && (
                          <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px', padding: '8px', background: 'white', borderRadius: '6px' }}>
                            <strong>Notes:</strong> {item.notes}
                          </div>
                        )}
                        <div style={{ fontSize: '13px', color: '#666' }}>Spiff: {item.spiff !== null ? `$${item.spiff}` : 'Pending'}</div>
                      </div>
                    ))}
                  </div>

                  {selectedOrder.notes && (
                    <div style={{ marginBottom: '20px', padding: '16px', background: '#FFF9E6', borderRadius: '12px', border: '1px solid #FDB515' }}>
                      <strong style={{ fontSize: '14px' }}>Order Notes:</strong>
                      <div style={{ marginTop: '8px', color: '#333', whiteSpace: 'pre-wrap' }}>{selectedOrder.notes}</div>
                    </div>
                  )}

                  {selectedOrder.signature_url && (
                    <div style={{ marginBottom: '20px', padding: '16px', background: '#FAFAFA', borderRadius: '12px' }}>
                      <strong style={{ fontSize: '14px' }}>Customer Signature:</strong>
                      {selectedOrder.signature_customer_name && (
                        <div style={{ marginTop: '6px', fontSize: '13px', color: '#374151' }}>
                          Signed by: {selectedOrder.signature_customer_name}
                        </div>
                      )}
                      {selectedOrder.completed_at && (
                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#6B7280' }}>
                          Completed at: {new Date(selectedOrder.completed_at).toLocaleString()}
                        </div>
                      )}
                      <SignatureImage
                        src={`${API}/orders/${selectedOrder.id}/signature-image`}
                        alt="Customer signature"
                        style={{ marginTop: '10px', width: '100%', maxWidth: '420px', border: '1px solid #E5E7EB', borderRadius: '8px', background: '#fff' }}
                      />
                    </div>
                  )}

                  <div style={{ background: '#FDB515', padding: '20px', borderRadius: '12px', color: '#000' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '600' }}>Subtotal:</span>
                      <span style={{ fontWeight: '700' }}>{selectedOrder.totals.subtotal !== null ? `$${selectedOrder.totals.subtotal}` : 'Pending'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '600' }}>Total Spiff:</span>
                      <span style={{ fontWeight: '700' }}>{selectedOrder.totals.spiffTotal !== null ? `$${selectedOrder.totals.spiffTotal}` : 'Pending'}</span>
                    </div>
                    <div style={{ borderTop: '2px solid rgba(0,0,0,0.2)', paddingTop: '8px', marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '18px', fontWeight: '800' }}>Total:</span>
                      <span style={{ fontSize: '18px', fontWeight: '800' }}>{selectedOrder.totals.total !== null ? `$${selectedOrder.totals.total}` : 'Pending'}</span>
                    </div>
                  </div>

                  {selectedOrder.adminNotes && (
                    <div style={{ marginTop: '20px', padding: '16px', background: '#FEE2E2', borderRadius: '12px' }}>
                      <strong>Admin Notes:</strong> {selectedOrder.adminNotes}
                    </div>
                  )}
                </div>
              ) : (
                // Edit Mode
                <div>
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Customer Information</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="form-group">
                        <label className="form-label">Customer Name</label>
                        <input
                          type="text"
                          className="form-input"
                          value={editFormData.customerName}
                          onChange={(e) => setEditFormData({ ...editFormData, customerName: e.target.value })}
                          data-testid="edit-customer-name"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-input"
                          value={editFormData.customerEmail}
                          onChange={(e) => setEditFormData({ ...editFormData, customerEmail: e.target.value })}
                          data-testid="edit-customer-email"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Phone</label>
                        <input
                          type="tel"
                          className="form-input"
                          value={editFormData.customerPhone}
                          onChange={(e) => setEditFormData({ ...editFormData, customerPhone: e.target.value })}
                          data-testid="edit-customer-phone"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Installation Date</label>
                        <input
                          type="date"
                          className="form-input"
                          value={editFormData.installationDate}
                          onChange={(e) => setEditFormData({ ...editFormData, installationDate: e.target.value })}
                          min={new Date().toISOString().split('T')[0]}
                          data-testid="edit-installation-date"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Store Invoice #</label>
                        <input
                          type="text"
                          className="form-input"
                          value={editFormData.storeInvoiceNumber}
                          onChange={(e) => setEditFormData({ ...editFormData, storeInvoiceNumber: e.target.value })}
                          placeholder="Optional"
                          data-testid="edit-store-invoice"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Address</label>
                      <textarea
                        className="form-input"
                        value={editFormData.customerAddress}
                        onChange={(e) => setEditFormData({ ...editFormData, customerAddress: e.target.value })}
                        rows={2}
                        data-testid="edit-customer-address"
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '700' }}>Items</h3>
                      <button type="button" onClick={addItem} className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '13px' }} data-testid="add-item-edit">
                        <Plus size={14} />
                        Add Item
                      </button>
                    </div>

                    {editFormData.items.map((item, index) => (
                      <div key={index} style={{ background: '#FAFAFA', padding: '16px', borderRadius: '12px', marginBottom: '12px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label" style={{ fontSize: '12px' }}>Category</label>
                            <Select value={item.productId} onValueChange={(value) => updateItem(index, 'productId', value)}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {products.map(product => (
                                  <SelectItem key={product.id} value={product.id}>{product.category}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label" style={{ fontSize: '12px' }}>Service</label>
                            <Select value={item.serviceId} onValueChange={(value) => updateItem(index, 'serviceId', value)} disabled={!item.productId}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                {item.productId && products.find(p => p.id === item.productId)?.services.map(service => (
                                  <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label" style={{ fontSize: '12px' }}>Qty</label>
                            <input
                              type="number"
                              className="form-input"
                              value={item.qty}
                              onChange={(e) => updateItem(index, 'qty', parseInt(e.target.value) || 1)}
                              min={1}
                            />
                          </div>

                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label" style={{ fontSize: '12px' }}>Price</label>
                            <div style={{ padding: '8px', background: 'white', borderRadius: '8px', fontWeight: '600', fontSize: '13px' }}>
                              ${item.price}
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="btn btn-danger"
                            style={{ padding: '8px' }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        {item.notes && (
                          <div style={{ marginTop: '8px', padding: '8px', background: 'white', borderRadius: '6px' }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Notes</div>
                            <div style={{ fontSize: '12px', color: '#333' }}>{item.notes}</div>
                          </div>
                        )}
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>Spiff: ${item.spiff}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <div className="form-group">
                      <label className="form-label">Order Notes</label>
                      <textarea
                        className="form-input"
                        value={editFormData.notes || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                        rows={3}
                        placeholder="Add notes about this order..."
                      />
                    </div>
                  </div>

                  <div style={{ background: '#FDB515', padding: '16px', borderRadius: '12px', color: '#000', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '600' }}>Subtotal:</span>
                      <span style={{ fontWeight: '700' }}>${total.toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '600' }}>Total Spiff:</span>
                      <span style={{ fontWeight: '700' }}>${totalSpiff.toFixed(2)}</span>
                    </div>
                    <div style={{ borderTop: '2px solid rgba(0,0,0,0.2)', paddingTop: '8px', marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '16px', fontWeight: '800' }}>Total:</span>
                      <span style={{ fontSize: '16px', fontWeight: '800' }}>${total.toFixed(2)}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      className="btn btn-primary"
                      style={{ flex: 1 }}
                      onClick={handleSaveOrder}
                      disabled={saving}
                      data-testid="save-order-button"
                    >
                      {saving ? 'Saving...' : 'Save & Resubmit Order'}
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ flex: 1 }}
                      onClick={() => setEditMode(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;
